import {openDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {
  type PkwEnvkvStatusResponseBody,
  useCheckPkwEnvkvAdvertisingStatusMutation,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {ApiError} from 'shared/web';

import {PkwEnvkvAdvertisingPreflightDialog} from '../components/PkwEnvkvAdvertisingPreflightDialog/PkwEnvkvAdvertisingPreflightDialog';
import {getPkwEnvkvAdvertisingCorrectionIssueCodes} from '../utils/getPkwEnvkvIssueLabels';
import {handleApiError} from '../utils/handleApiError';
import {useTenant} from './useTenant';

// Distinct ids: the closing warning must never remove a blocked dialog opened after Continue.
const WARNING_DIALOG_ID = 'pkw-envkv-advertising-warning';
const BLOCKED_DIALOG_ID = 'pkw-envkv-advertising-blocked';

interface ResolvePkwEnvkvGateOptions {
  vehicleId: string;
  status: PkwEnvkvStatusResponseBody;
  fetchCurrentStatus: (vehicleId: string) => Promise<PkwEnvkvStatusResponseBody>;
  /**
   * Set for the re-check after the user's Continue in the warning dialog: a
   * vehicle that is still YELLOW then passes without a second warning.
   */
  isWarningConfirmed?: boolean;
}

/**
 * Resolves with `true` when the advertising action may proceed. A YELLOW
 * vehicle resolves after the user's decision in the warning dialog; a RED
 * vehicle resolves with `false` right away because the dialog only offers
 * corrections, never a way to continue.
 */
function resolvePkwEnvkvGate(options: ResolvePkwEnvkvGateOptions): Promise<boolean> {
  const {status, vehicleId} = options;
  if (!status.envkvRequired) {
    return Promise.resolve(true);
  }
  if (status.advertisingStatus === null) {
    // The backend omits the advertising status only when EnVKV is not
    // required, so a required vehicle without one cannot be validated.
    showNotification.error(i18n.t('page.pkwEnvkv.advertisingIssues.unknown'));
    return Promise.resolve(false);
  }

  return match(status.advertisingStatus)
    .with('GREEN', () => Promise.resolve(true))
    .with('YELLOW', () =>
      options.isWarningConfirmed ? Promise.resolve(true) : confirmPkwEnvkvWarning(options)
    )
    .with('RED', () => {
      openDialog(
        <PkwEnvkvAdvertisingPreflightDialog
          variant="blocked"
          vehicleId={vehicleId}
          issueCodes={getPkwEnvkvAdvertisingCorrectionIssueCodes(status)}
        />,
        {
          id: BLOCKED_DIALOG_ID,
          title: i18n.t('page.pkwEnvkv.title'),
        }
      );

      return Promise.resolve(false);
    })
    .exhaustive();
}

/**
 * Opens the YELLOW warning. The vehicle data can change while the warning
 * stays open, so Continue does not trust the status the warning was opened
 * with: it fetches the current status and resolves from that one. Closing the
 * warning any other way resolves with `false`.
 */
function confirmPkwEnvkvWarning(options: ResolvePkwEnvkvGateOptions): Promise<boolean> {
  const {fetchCurrentStatus, status, vehicleId} = options;

  return new Promise<boolean>((resolve) => {
    openDialog(
      <PkwEnvkvAdvertisingPreflightDialog
        variant="warning"
        vehicleId={vehicleId}
        issueCodes={status.issues}
        onContinue={() =>
          resolve(
            fetchCurrentStatus(vehicleId)
              .then((currentStatus) =>
                resolvePkwEnvkvGate({...options, status: currentStatus, isWarningConfirmed: true})
              )
              .catch((error: ApiError) => {
                handleApiError(error);
                return false;
              })
          )
        }
      />,
      {
        id: WARNING_DIALOG_ID,
        title: i18n.t('page.pkwEnvkv.title'),
        // A promise settles once, so this is a no-op after Continue.
        onCloseComplete: () => resolve(false),
      }
    );
  });
}

/**
 * Fetches the current Pkw-EnVKV status immediately before an advertising
 * action. GREEN passes, YELLOW requires explicit confirmation, and RED opens a
 * blocking dialog with the available correction actions. A failed status
 * request also blocks the action; the backend remains authoritative.
 *
 * `runPkwEnvkvPreflight` resolves with whether the action may proceed and
 * calls `onPassed` right before resolving with `true`.
 */
export function usePkwEnvkvAdvertisingPreflight() {
  const {tenant, isLoading: isTenantLoading} = useTenant();
  const [checkPkwEnvkvAdvertisingStatus, {isLoading: isChecking}] =
    useCheckPkwEnvkvAdvertisingStatusMutation();

  const fetchCurrentStatus = (vehicleId: string) =>
    checkPkwEnvkvAdvertisingStatus({vehicleId}).unwrap();

  const runPkwEnvkvPreflight = async (
    vehicleId: string,
    onPassed?: () => void
  ): Promise<boolean> => {
    const isPassed =
      tenant !== undefined && tenant.country !== 'DEU'
        ? true
        : await fetchCurrentStatus(vehicleId)
            .then((status) => resolvePkwEnvkvGate({vehicleId, status, fetchCurrentStatus}))
            .catch((error: ApiError) => {
              handleApiError(error);
              return false;
            });

    if (isPassed) {
      onPassed?.();
    }

    return isPassed;
  };

  return {runPkwEnvkvPreflight, isChecking: isChecking || isTenantLoading};
}
